<template>
    <div class="min-h-screen flex flex-col bg-gray-100">
        <!-- Mobile Menu Button -->
        <div class="bg-white p-4 border-b border-gray-200 flex justify-end md:hidden">
            <button @click="isSidebarOpen = !isSidebarOpen"
                class="text-gray-600 focus:outline-none text-2xl transition duration-300 ease-in-out">
                <font-awesome-icon :icon="isSidebarOpen ? 'times' : 'bars'" />
            </button>
        </div>

        <div class="flex flex-1">
            <!-- Sidebar for navigation -->
            <aside
                :class="[isSidebarOpen ? 'block' : 'hidden', 'md:block', 'w-64 bg-white border-r border-gray-200 shadow-md transition-all duration-300 ease-in-out']">
                <div v-if="isLoggedIn" class="p-6 text-center">
                    <img :src="user.avatar" alt="User Avatar"
                        class="mx-auto rounded-full w-24 h-24 object-cover shadow-md" />
                    <h2 class="mt-4 text-lg font-semibold text-gray-900">{{ user.full_name }}</h2>
                    <p class="text-sm text-gray-500">{{ user.email }}</p>
                </div>

                <nav class="mt-6">
                    <ul class="space-y-2">
                        <li @click="setActiveSection('settings')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'settings' }">
                            <span class="nav-icon"><font-awesome-icon icon="cog" /></span>
                            {{ $translate('Settings') }}
                        </li>
                        <li @click="setActiveSection('payments')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'payments' }">
                            <span class="nav-icon"><font-awesome-icon icon="file-invoice" /></span>
                            {{ $translate('Payments & Invoices') }}
                        </li>
                        <li @click="setActiveSection('billingAddress')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'billingAddress' }">
                            <span class="nav-icon"><font-awesome-icon icon="address-card" /></span>
                            {{ $translate('Billing Address') }}
                        </li>
                        <li @click="setActiveSection('preferences')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'preferences' }">
                            <span class="nav-icon"><font-awesome-icon icon="sliders-h" /></span>
                            {{ $translate('Preferences') }}
                        </li>
                        <li @click="setActiveSection('delete')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'delete' }">
                            <span class="nav-icon"><font-awesome-icon icon="trash" /></span>
                            {{ $translate('Delete Account') }}
                        </li>


                        <!--   <div class="border-t border-gray-200 my-4"></div>

                        <li @click="setActiveSection('certificates')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'certificates' }">
                            <span class="nav-icon"><font-awesome-icon icon="certificate" /></span>
                            {{ $translate('Certificates') }}
                        </li>
                        <li @click="setActiveSection('coupons')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'coupons' }">
                            <span class="nav-icon"><font-awesome-icon icon="ticket" /></span>
                            {{ $translate('Coupons') }}
                        </li>
                        <li @click="setActiveSection('tickets')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'tickets' }">
                            <span class="nav-icon"><font-awesome-icon icon="calendar-check" /></span>
                            {{ $translate('Tickets') }}
                        </li>
                        <li @click="setActiveSection('vouchers')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'vouchers' }">
                            <span class="nav-icon"><font-awesome-icon icon="square-check" /></span>
                            {{ $translate('Vouchers & Gift Cards') }}
                        </li>
                        <li @click="setActiveSection('partners')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'partners' }">
                            <span class="nav-icon"><font-awesome-icon icon="handshake" /></span>
                            {{ $translate('Partner Program') }}
                        </li>
                        <li @click="setActiveSection('contracts')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'contracts' }">
                            <span class="nav-icon"><font-awesome-icon icon="file-contract" /></span>
                            {{ $translate('Contracts') }}
                        </li>-->
                    </ul>
                </nav>

                <div class="mt-auto p-6">
                    <button @click="handleLogout"
                        class="w-full bg-red-500 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-600 transition duration-300">
                        {{ $translate('Logout') }}
                    </button>
                    <button @click="handleHome"
                        class="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition duration-300">
                        {{ $translate('Back to Event List') }}
                    </button>
                </div>
            </aside>

            <!-- Main content area -->
            <main class="flex-1 p-6 bg-gray-100 shadow-sm rounded-lg">
                <!-- Conditionally render AccountPayments.vue only when payments tab is active -->
                <component v-if="activeSectionComponent" :is="activeSectionComponent" :user="user"></component>
            </main>
        </div>
    </div>
</template>

<script>
import { getUserData, logout } from '@/utils/cookie';
import { getSectionFromPath, getSystemPath } from '@/utils/helper';
import AccountSettings from './account/AccountSettings.vue';
import AccountCertificates from './account/AccountCertificates.vue';
import AccountPreferences from './account/AccountPreferences.vue';
import AccountPayments from './account/AccountPayments.vue';
import BillingAddress from './account/BillingAddress.vue';
import AccountDelete from './account/AccountDelete.vue';

export default {
    name: 'UserAccount',
    components: {
        AccountSettings,
        AccountCertificates,
        AccountPreferences,
        AccountPayments,
        AccountDelete,
        BillingAddress,
    },
    data() {
        return {
            isLoggedIn: false,
            user: null,
            activeSection: 'settings', // Default section
            isSidebarOpen: false, // Sidebar toggle state
        };
    },
    computed: {
        activeSectionComponent() {
            switch (this.activeSection) {
                case 'certificates':
                    return 'AccountCertificates';
                case 'preferences':
                    return 'AccountPreferences';
                case 'payments':
                    return 'AccountPayments';
                case 'delete':
                    return 'AccountDelete';
                case 'billingAddress':
                    return 'BillingAddress';
                default:
                    return 'AccountSettings';
            }
        },
    },
    created() {
        const user = getUserData(true);
        if (user) {
            this.isLoggedIn = true;
            this.user = user;
        }

        // Set the active section based on the current route
        this.setActiveSectionFromRoute();
    },
    watch: {
        '$route'() {
            // Watch for route changes and update the active section
            this.setActiveSectionFromRoute();
        },
    },
    methods: {
        setActiveSectionFromRoute() {
            const routeId = this.$route.params.id;
            const section = getSectionFromPath(routeId);
            if (section) {
                this.activeSection = section;
            } else {
                this.activeSection = 'settings'; // Default to settings if no match
            }
        },
        setActiveSection(section) {
            this.activeSection = section;
            this.isSidebarOpen = false; // Close the sidebar on mobile when a section is clicked
            const sectionName = getSystemPath(section);
            // Update the route to reflect the section change
            this.$router.push({
                name: 'UserAccount',
                params: { id: sectionName },
            });
        },
        handleLogout() {
            logout();
            this.$router.push({ name: 'Login' });
        },
        handleHome() {
            this.$router.push({ name: 'EventList' });
        },
    },
};
</script>

<style scoped>
.nav-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    color: #4b5563;
    border-left: 4px solid transparent;
    transition: background-color 0.3s, color 0.3s, border-color 0.3s;
}

.nav-item:hover {
    background-color: #f3f4f6;
    color: #1f2937;
}

.nav-item-active {
    background-color: #e5f3ff;
    color: #2563eb;
    border-left-color: #2563eb;
}

.nav-icon {
    width: 1.5rem;
    /* Fixed width for icons */
    display: flex;
    justify-content: center;
    margin-right: 1rem;
}

button {
    cursor: pointer;
}

@media (min-width: 768px) {
    aside {
        display: block !important;
    }
}
</style>